/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import useAbortController from './useAbortController';
import axiosCall from '../../services/axios';
import { useLoanContext } from '../../contexts/LoanContext';
import { useAuthContext } from '../../contexts/AuthContext';

type UseLoanServicing = {
  loanServicing: UnknownLoanServicings;
  loadingLoanServicing: boolean;
  updateLoanServicing: (
    fields: Record<string, any>,
    setErrors: Dispatch<SetStateAction<Record<string, any>>>,
  ) => Promise<void>;
  reloadLoanServicing: () => void;
};

const useLoanServicing = (path = '', loan = ''): UseLoanServicing => {
  const { get } = useAbortController();
  const { token } = useAuthContext();
  const queryClient = useQueryClient();
  const { loanNumber } = useLoanContext();
  const ln = useMemo(() => loan || loanNumber, [loan, loanNumber]);
  const queryKey = useMemo(() => ['loanServicings', ln, path, token], [ln, path, token]);

  // Fetch communication data using react-query
  const { data, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: async () => {
      if (!token || !ln) return { data: null };
      try {
        const request = {
          url: `live/loan/${ln}/servicing${path}`,
          method: 'GET',
        };
        const headers = {
          'Session-Token': token,
        };
        const response = await axiosCall(request, undefined, headers);
        console.log(`useLoanServicing.response: `, response);
        return { data: response };
      } catch (error) {
        console.error(`useLoanServicing.caught: ${error.message}`);
        // Simulated fetch call for testing, TODO: Remove before public release.
        return {
          data: null,
        };
      }
    },
  });

  const reloadLoanServicing = useCallback(() => queryClient.invalidateQueries({ queryKey }), [queryClient, queryKey]);

  const updateLoanServicing = useCallback(
    async (fields, setErrors) => {
      if (!token) throw new Error('!token');
      if (!fields || typeof fields !== 'object') {
        throw new Error('!fields');
      }
      const errors = {
        generic: '',
      };
      setErrors((prev) => ({ ...prev, ...errors }));
      if (Object.values(errors).filter((v) => v).length > 0) return;

      const request = {
        url: `live/loan/${loanNumber}/servicing${path}`,
        method: 'POST',
        data: fields,
      };
      const config = get();
      const headers = {
        'Session-Token': token,
      };
      const response = await axiosCall(request, config, headers);
      console.log(`useLoanServicing.response: `, response);
      reloadLoanServicing();
      return response;
    },
    [loanNumber, path, get, reloadLoanServicing, token],
  );

  return {
    loanServicing: !isLoading && data && 'data' in data && data.data ? data.data : null,
    loadingLoanServicing: isLoading,
    updateLoanServicing,
    reloadLoanServicing,
  };
};

export default useLoanServicing;
